import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { AdminUserDetail, UserCasePage, UserEntitlementList, UserId } from '../_shared/types';
import { toAdminUserDetail } from '../_shared/wire';

/**
 * Three independent reads across three services, never one composite.
 *
 * That is what gives the page its per-panel error boundary: `backend-finance`
 * being unreachable takes the entitlement table down and leaves the profile, the
 * role control, the biography, and the case history usable. A composite read
 * would blank the page for one peer's outage.
 *
 * **`staleTime` is `0` on all three.** This is a detail route, and every one of
 * its panels is the subject of a decision taken on the same screen — a role
 * change, a grant, a block. A cached answer here would show the operator the
 * state they just left rather than the one they just created.
 *
 * The three keys mirror their own REST URLs, so a mutation elsewhere in the
 * console that moves one of these records invalidates the exact tuple the panel
 * registered.
 */

export function adminUserKey(userId: UserId) {
  return key('auth', 'adminUser', userId);
}

export function userEntitlementsKey(userId: UserId) {
  return key('finance', 'userEntitlements', userId);
}

export function userCasesKey(userId: UserId) {
  return key('support', 'cases', { raiserUserId: userId });
}

export function useAdminUser(userId: UserId) {
  return useQuery({
    queryKey: adminUserKey(userId),
    queryFn: () =>
      api
        .get<Parameters<typeof toAdminUserDetail>[0]>('auth', `/admin/users/${userId}`)
        .then(toAdminUserDetail) satisfies Promise<AdminUserDetail>,
    staleTime: STALE_TIME.detail,
  });
}

export function useUserEntitlements(userId: UserId) {
  return useQuery({
    queryKey: userEntitlementsKey(userId),
    queryFn: () => api.get<UserEntitlementList>('finance', `/admin/users/${userId}/entitlements`),
    staleTime: STALE_TIME.detail,
  });
}

/**
 * The account's case history.
 *
 * **`raiserUserId` is the whole filter.** The section's own line says the table
 * holds cases this account opened *or* that were opened against it, and the
 * queue answers the first half; a second read on `subjectOwnerUserId` would be a
 * second request the pack does not draw and a merge this surface has no ordering
 * rule for. The queue link beside the heading is the way to the rest.
 *
 * No `pageSize` and no `cursor` are sent: the strip has no traversal control,
 * the service owns its page size, and the operator walks the full record in the
 * queue rather than here.
 */
export function useUserCases(userId: UserId) {
  return useQuery({
    queryKey: userCasesKey(userId),
    queryFn: () => {
      const params = new URLSearchParams({ raiserUserId: userId });
      return api.get<UserCasePage>('support', `/admin/cases?${params.toString()}`);
    },
    staleTime: STALE_TIME.detail,
  });
}
